import { useGetDossierDetailsById } from "@/entities/dossier-details";
import { useDossierId } from "@/features/dossier-details";
import { Filter, Filters, initialFilters, Tijdlijn } from "@/features/tijdlijn";
import { ArchiveVisibilityProvider, HideButtonProvider, Sidebar, SidebarHeader, SidebarTrigger } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { useIsScrolled } from "@/shared/hooks/use-is-scrolled";
import { cn } from "@/shared/utils";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Dispatch, type ReactNode, SetStateAction, useEffect, useRef, useState } from "react";
import { Outlet } from "react-router";
import { useCloseDossierStore } from "./close-dossier/close-dossier-store";
import { DossierAfgerondDialog } from "./dossier-afgerond-dialog";

const FADE_IN_OUT_DURATION = 150; // in ms

interface ReadonlyWrapperProps {
    readonly: boolean;
    children: ReactNode;
}

function ReadonlyWrapper({ readonly, children }: ReadonlyWrapperProps) {
    if (readonly) {
        return (
            <ArchiveVisibilityProvider>
                <HideButtonProvider>{children}</HideButtonProvider>
            </ArchiveVisibilityProvider>
        );
    }
    return <>{children}</>;
}

export default function DossierLayout() {
    const id = useDossierId();
    const { data: dossierDetails, isPending, error } = useGetDossierDetailsById(id);
    const prefersReducedMotion = useReducedMotion();

    const [filters, setFilters] = useState([...initialFilters].sort((a, b) => a.order - b.order));
    const [showClosedDialog, setShowClosedDialog] = useState(false);
    const prevStatusRef = useRef(dossierDetails?.status);
    const currentlyClosingDossierNummer = useCloseDossierStore((state) => state.currentlyClosingDossierNummer);
    const setCurrentlyClosingDossierNummer = useCloseDossierStore((state) => state.setCurrentlyClosingDossierNummer);
    const readonly = dossierDetails?.status === "Gesloten";

    useEffect(() => {
        if (prevStatusRef.current === "Open" && dossierDetails?.status === "Gesloten") {
            if (currentlyClosingDossierNummer === id) {
                // the current user was the one that closed this dossier, don't show the dialog
                setCurrentlyClosingDossierNummer(null);
            } else {
                setShowClosedDialog(true);
            }
        }
        prevStatusRef.current = dossierDetails?.status;
    }, [dossierDetails?.status, currentlyClosingDossierNummer, setCurrentlyClosingDossierNummer, id]);

    if (isPending) return null;

    if (error) {
        return (
            <ErrorDisplay
                error={error}
                variant="section"
                title="Fout bij het laden van het dossier"
                onRetry={() => window.location.reload()}
            />
        );
    }

    if (!dossierDetails) return null;

    const activeFilters = filters.filter((filter) => filter.active);

    if (prefersReducedMotion) {
        return (
            <ReadonlyWrapper readonly={readonly}>
                <DossierAfgerondDialog isOpen={showClosedDialog} dossierId={id} />
                <NotAnimatedDossier
                    id={id}
                    activeFilters={activeFilters}
                    filters={filters}
                    setFilters={setFilters}
                    omschrijving={dossierDetails.omschrijving}
                    readOnly={readonly}
                />
            </ReadonlyWrapper>
        );
    } else {
        return (
            <ReadonlyWrapper readonly={readonly}>
                <DossierAfgerondDialog isOpen={showClosedDialog} dossierId={id} />
                <AnimatedDossier
                    id={id}
                    activeFilters={activeFilters}
                    filters={filters}
                    setFilters={setFilters}
                    omschrijving={dossierDetails.omschrijving}
                    readOnly={readonly}
                />
            </ReadonlyWrapper>
        );
    }
}

type DossierProps = {
    id: string;
    activeFilters: Filter[];
    filters: Filter[];
    setFilters: Dispatch<SetStateAction<Filter[]>>;
    omschrijving: string;
    readOnly: boolean;
};

function AnimatedDossier({ id, activeFilters, filters, setFilters, omschrijving, readOnly }: DossierProps) {
    const { ref, isScrolled } = useIsScrolled();

    return (
        <motion.div
            key={id}
            animate={{ opacity: 1 }}
            initial={{ opacity: 0 }}
            exit={{ opacity: 0 }}
            className={`z-10 flex max-h-full w-full flex-row`}
            transition={{
                type: "tween",
                duration: FADE_IN_OUT_DURATION / 1000,
                ease: "easeOut",
            }}
        >
            <AnimatePresence>
                <Sidebar className="bg-background flex border-r">
                    <div className="bg-background rounded-r-m p-auto absolute top-5 right-0 translate-x-[70%]">
                        <SidebarTrigger />
                    </div>
                    <SidebarHeader className={cn(isScrolled.top && "shadow-elevated-2")}>
                        <h2 className="text-xxxl leading-m text-content-emphasis font-medium">{omschrijving}</h2>
                        <h3 className="leading-m text-content-emphasis text-xl font-medium">Tijdlijn</h3>
                        <Filters filters={filters} setFilters={setFilters} />
                    </SidebarHeader>
                    <Tijdlijn dossierNummer={id} activeFilters={activeFilters} readOnly={readOnly} ref={ref} />
                </Sidebar>
            </AnimatePresence>
            <motion.div
                key={`dossier-${id}`}
                initial={{ opacity: 0, top: -200 }}
                animate={{ opacity: 1, top: 0 }}
                transition={{
                    type: "spring",
                    stiffness: 800,
                    damping: 50,
                    mass: 2,
                    bounce: 0.4,
                }}
                className="m-l relative flex max-h-[calc(100%-48px)] w-full self-start overflow-y-auto rounded-l filter-(--drop-shadow-elevated-1) xl:w-[812px]"
            >
                <Outlet />
            </motion.div>
        </motion.div>
    );
}

function NotAnimatedDossier({ id, activeFilters, filters, setFilters, omschrijving, readOnly }: DossierProps) {
    const { ref, isScrolled } = useIsScrolled();

    return (
        <div key={id} className="z-10 flex max-h-full w-full flex-row">
            <Sidebar className="bg-background flex border-r">
                <div className="bg-background rounded-r-m p-auto absolute top-5 right-0 translate-x-[70%]">
                    <SidebarTrigger />
                </div>
                <SidebarHeader className={cn(isScrolled.top && "shadow-elevated-2")}>
                    <h2 className="text-xxxl leading-m text-content-emphasis font-medium">{omschrijving}</h2>
                    <h3 className="leading-m text-content-emphasis text-xl font-medium">Tijdlijn</h3>
                    <Filters filters={filters} setFilters={setFilters} />
                </SidebarHeader>
                <Tijdlijn dossierNummer={id} activeFilters={activeFilters} readOnly={readOnly} ref={ref} />
            </Sidebar>
            <div
                key={`dossier-${id}`}
                className="m-l relative flex max-h-[calc(100%-48px)] w-full self-start overflow-y-auto rounded-l filter-(--drop-shadow-elevated-1) xl:w-[812px]"
            >
                <Outlet />
            </div>
        </div>
    );
}
